const app = require('../app.json');
const { get } = require('lodash');

const toRGB = (color) => {
    const { style } = new Option();
    style.color = color;
    return style.color;
}

const rgba = (color, opacity = 0.1) => {
  const col = toRGB(color);

  const raw = col.replace('rgb(', 'rgba(').replace(')', '');

  return `${raw}, ${opacity})`;
}

module.exports = {
  highlight: app.color,
  highlightAlt: '#663445',
  labelColor: '#4d4d4d',
  inputBorder: '#efefef',
  disabledColor: '#bed3d7',
  disabledLight: '#f5f5f5',
  success: 'green',
  error: 'red',
  errorLight: 'rgba(255,0,0,0.2)',
  warning: 'orange',
  warningLight: 'rgba(255,165,0,0.2)',
  lightBlue: 'rgba(0,189,255,0.2)',
  orderReceived: '#0051ff',
  maroon: 'rgba(176,0,177,0.3)',
  lightBackground: 'white',
  black: 'black',
  white: 'white',
  overlay: 'rgba(0,0,0,0.3)',
  table0: `${rgba(app.color)}`,
  tagDark: '#4d4d4d',
  hrBorder: '#efefef',
  tableBorder: '#d6d6d6',
  buttonDark: '#666666',
  footerBg: '#9a9899',
  eventBg: '#663445',
  eventBgDark: '#4d2231',
  cardOutline: '#9e9e9e',
  infoRow: '#f0fbff',
  lightText: '#aeaeae',
}

module.exports.rgba = rgba;

// --- Card #256: Required COD Payment Enforcement -----------------------------
// Shared predicate + Order-ID style used across the orders list and schedule so
// the orange treatment stays consistent. Attached to the exported colors object
// (like `rgba`) so every existing `import colors from '../../_shared/colors'`
// site can call `colors.orderRequiresPayment(order)` / `colors.getOrderIdStyle(order)`.

// A COD order's payment obligation is satisfied when a Cash or Check payment is
// recorded (methodSelect cash/check + its reference id filled) OR an admin has
// applied an override. Cash/Check ONLY — Net Terms / Samples do NOT satisfy it.
const orderPaymentSatisfied = (order) => {
  const method = get(order, 'data.billing.methodSelect');
  const ref = get(order, 'data.billing.methodSelectId');
  const hasValidPayment = (method === 'cash' || method === 'check') && !!(ref && String(ref).trim());
  const overridden = !!get(order, 'data.order.paymentOverride');
  return hasValidPayment || overridden;
};

// Card #256 — order types that never carry a payment obligation:
//   rej_return  — Rejection Return. Round 5 (Danielle 2026-07-30: "Rejection
//                 Returns should never show Payment required fields").
//   cash_return — Reconciliation Return. Round 6 (Danielle 2026-08-03: "can we
//                 remove the Payment Required checkbox from Reconciliation
//                 Returns").
// Deliberately NOT exempt (Bruce's call, round 6): 'cash_pickup' — a "Payment
// Pickup" exists to collect money, so it keeps the checkbox — and
// 'other_return', which was not asked for.
//
// Suppressing on ORDER TYPE repairs rows written before each round with no data
// migration: the auto-created rej_return child clones the parent's data.order
// wholesale, and a cash_return can hold an explicit paymentRequired = false that
// the order form still reads as "already flagged".
//
// ONE predicate deliberately drives BOTH the payment-requirement behaviour and
// the orange Order ID / "Payment Required" tag, and lives here rather than in the
// order screen so the Orders list, the Schedule and the order form all read it.
// The list is duplicated — it cannot be shared across the wire — in
// server/src/controllers/orders/utils.js (same constant name); the two MUST stay
// in LOCKSTEP, or the server rejects a completion the UI gave no way to satisfy.
const PAYMENT_EXEMPT_ORDER_TYPES = ['rej_return', 'cash_return'];

// An order with a missing / unrecognised order type is NOT exempt.
const isPaymentExemptReturn = (order) => (
  PAYMENT_EXEMPT_ORDER_TYPES.includes(get(order, 'data.order.orderType'))
);

// True when the per-order "Payment Required?" flag is set AND payment has not
// yet been recorded (nor overridden) — i.e. the Order ID should show orange.
//
// ***NOT SELF-GATING.*** Card #256 round 7 added a master switch — the Edit
// Settings entry `orders.paymentRequirementEnabled` (default OFF everywhere) —
// which suppresses the ENTIRE payment-requirement feature, this orange
// treatment included. That switch is applied by the CALLERS, not here:
//   1. app/src/dashboard/orders/orders.component.jsx  (COLS, outgoing column)
//   2. app/src/dashboard/orders/orders.component.jsx  (COLS, incoming column)
//   3. app/src/dashboard/schedule/schedule.component.jsx (getCols, driver view)
// Each reads `getConfig('orders.paymentRequirementEnabled', false)` once per
// column build and ANDs it in front of this call. ANY NEW CALLER MUST DO THE
// SAME — otherwise the orange Order ID / "Payment Required" pill leaks onto a
// screen with the feature switched off.
//
// Deliberate: this module is required by a large number of *.styles.js modules,
// so it is kept free of any dependency on ../actions/utils (getConfig) to avoid
// a circular import. The trade is call-site duplication, which this comment and
// the three-site list above exist to police.
//
// `getOrderIdStyle` below wraps this predicate and has ZERO consumers anywhere
// in app/, ext/ or native/ (verified by grep, round 7) — it is dead code. If it
// is ever revived it inherits the same obligation: gate it at the call site.
const orderRequiresPayment = (order) => (
  !isPaymentExemptReturn(order)
    && !!get(order, 'data.order.paymentRequired')
    && !orderPaymentSatisfied(order)
);

// Inline style for an Order ID that requires-and-lacks payment (orange, bold).
const getOrderIdStyle = (order) => (
  orderRequiresPayment(order)
    ? { color: module.exports.warning, fontWeight: 'bold' }
    : {}
);

module.exports.PAYMENT_EXEMPT_ORDER_TYPES = PAYMENT_EXEMPT_ORDER_TYPES;
module.exports.isPaymentExemptReturn = isPaymentExemptReturn;
// Round-5 name, kept as an alias so the order screen's own predicate keeps
// resolving whichever name it calls. Prefer isPaymentExemptReturn in new code —
// this one no longer describes what it matches (it is now true for cash_return
// as well as rej_return).
module.exports.isRejectionReturn = isPaymentExemptReturn;
module.exports.orderPaymentSatisfied = orderPaymentSatisfied;
module.exports.orderRequiresPayment = orderRequiresPayment;
module.exports.getOrderIdStyle = getOrderIdStyle;